import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ChannelContext, UserContext } from '../../../context';
import {
  OrganisationTag,
  Story,
  StoryAccess,
  StoryAutoNotification,
  StoryCardLimit,
  StoryCardOrder,
  StoryCardTeaserImageStyle,
  StoryCreateFieldsInput,
  StoryDisplayItemUpdatedTime,
  StoryHighlightingCommentsMode,
  StoryType,
  StoryUpdateArgs,
  UserPreview,
  storyApi,
} from '../../../services';
import {
  Button,
  ButtonType,
  CardProps,
  FeedFollowBehavior,
  FileAcceptType,
  FilePreview,
  FileUpload,
  FilterValue,
  GalleryItem,
  IconLabelWithActive,
  Modal,
  RestrictDuplicateUrlOfCardsInTimeFrame,
  StoryTabType,
  TextAreaField,
  optArrItem,
  useAppDispatch,
  useFilter,
  useRefreshFeeds,
} from '../../../shared';
import { updateStory } from '../../../slices';
import {
  AUTO_NOTIFICATION_MESSAGE_MIN_LIMIT,
  AUTO_NOTIFICATION_TITLE_MIN_LIMIT,
  CreateStorySettings,
  DisplayProps,
  OtherProps,
} from './CreateStorySettings';
import { StorySync, StorySyncState } from './StorySync';

import classes from './CreateStoryModal.module.scss';

interface CreateStoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  story: Story;
  storyTabType?: StoryTabType;
  callback?: (story: Story) => void;
}

export interface StorySettings {
  display: FilterValue[];
  card: FilterValue[];
  tags: {
    external: OrganisationTag[];
    setExternal: (items: OrganisationTag[]) => void;
    externalStatus: OrganisationTag[];
    setExternalStatus: (items: OrganisationTag[]) => void;
    internal: OrganisationTag[];
    setInternal: (items: OrganisationTag[]) => void;
  };
  access: FilterValue[];
  writeAccess: {
    userTags: OrganisationTag[];
    setUserTags: (userTags: OrganisationTag[]) => void;
    users: UserPreview[];
    setUsers: (users: UserPreview[]) => void;
  };
  other: FilterValue[];
  autoNotification: {
    autoNotification: StoryAutoNotification;
    setAutoNotification: Dispatch<SetStateAction<StoryAutoNotification>>;
  };
  sync: {
    storySync: StorySyncState;
    setStorySync: Dispatch<SetStateAction<StorySyncState>>;
    setTitle: Dispatch<SetStateAction<string>>;
    setSubtitle: Dispatch<SetStateAction<string>>;
    setGalleryItems: Dispatch<SetStateAction<GalleryItem[]>>;
  };
}

export const CreateStoryModal: FunctionComponent<CreateStoryModalProps> = memo(
  ({ isOpen, onClose, story, storyTabType, callback }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const { refreshFeeds } = useRefreshFeeds({ scrollTop: false, storyTabType });

    const [storyCreate] = storyApi.endpoints.storyCreate.useMutation();

    const { id: channelId, localeId: channelLocaleId } = useContext(ChannelContext).channel;

    const { channelRestrictDuplicateUrlOfCardsGQL } = useContext(UserContext).userInfo.userData;

    const {
      id: storyId,
      type: storyType,
      title: storyTitle,
      subtitle: storySubtitle,
      pinned: storyPinned,
      pinned2: storyPinned2,
      pinned3: storyPinned3,
      pinned4: storyPinned4,
      pinned5: storyPinned5,
      image: storyImage,
      settings: storySettings,
      localeId: storyLocaleId,
      externalTags: storyExternalTags,
      externalStatusTags: storyExternalStatusTags,
      internalTags: storyInternalTags,
      writeAccess: storyWriteAccess,
      sources: storySources,
    } = story;

    const {
      allowCardReactions: storyAllowCardReactions,
      allowComments: storyAllowComments,
      allowCardMentions: storyAllowCardMentions,
      allowSharing: storyAllowSharing,
      autoNotification: storyAutoNotification,
      allowAccessToReader: storyAllowAccessToReader,
      allowAccessToEditorLimited: storyAllowAccessToEditorLimited,
      includeInWebappNewsFeed: storyIncludeInWebappNewsFeed,
      includeInNewsFeed: storyIncludeInNewsFeed,
      includeInMixTab: storyIncludeInMixTab,
      includeInMixTab2: storyIncludeInMixTab2,
      includeInMixTab3: storyIncludeInMixTab3,
      includeInMixTab4: storyIncludeInMixTab4,
      includeInMixTab5: storyIncludeInMixTab5,
      horizontalDisplayView: storyHorizontalDisplayView,
      shouldUpdatePostedTimeByComments: storyShouldUpdatePostedTimeByComments,
      highlightingComments: storyHighlightingComments,
      cardOrder: storyCardOrder,
      displayItemUpdatedTime: storyDisplayItemUpdatedTime,
      cardStyles: storyCardStyles,
      showAllAuthors: storyShowAllAuthors,
      shouldSyncItemPositionsOnConnectedStories: storyShouldSyncItemPositionsOnConnectedStories,
      cardLimit: storyCardLimit,
      feedFollowBehavior: storyFeedFollowBehavior,
      shouldRestrictDuplicateUrlOfCardsInTimeFrame:
        storyShouldRestrictDuplicateUrlOfCardsInTimeFrame,
    } = { ...storySettings };

    const editMode = useMemo(() => Boolean(storyId), [storyId]);

    const isStandard = useMemo(
      () => [StoryType.STANDARD, StoryType.SYNC].includes(storyType),
      [storyType]
    );

    const standardOrEdit = useMemo(() => isStandard || editMode, [editMode, isStandard]);

    const noStandardNorEdit = useMemo(() => !isStandard && !editMode, [editMode, isStandard]);

    const [galleryItems, setGalleryItems] = useState<GalleryItem[]>(
      storyImage ? [{ image: storyImage }] : []
    );

    const [activeSettingsTabIndex, setActiveSettingsTabIndex] = useState(0);

    const galleryItemImage = useMemo(() => galleryItems[0]?.image, [galleryItems]);

    const [externalTags, setExternalTags] = useState<OrganisationTag[]>(storyExternalTags ?? []);

    const [externalStatusTags, setExternalStatusTags] = useState<OrganisationTag[]>(
      storyExternalStatusTags ?? []
    );

    const [internalTags, setInternalTags] = useState<OrganisationTag[]>(storyInternalTags ?? []);

    const [userTags, setUserTags] = useState<OrganisationTag[]>(storyWriteAccess?.userTags ?? []);

    const [users, setUsers] = useState<UserPreview[]>(storyWriteAccess?.users ?? []);

    const [autoNotification, setAutoNotification] = useState<StoryAutoNotification>(
      storyAutoNotification ?? {
        enabled: false,
        titleDefault: '',
        messageDefault: '',
        titlePrefix: '',
      }
    );

    const [storySync, setStorySync] = useState<StorySyncState>({
      enabled: !isStandard || Boolean(storySources?.length),
      sources: (storySources ?? []).map(
        ({ channel: { id: channelId }, story: { id: storyId } }) => ({
          channelId,
          storyId,
          fetchLastCards: 0,
        })
      ),
    });

    const [title, setTitle] = useState<string>(editMode ? storyTitle : '');

    const [subtitle, setSubtitle] = useState<string>(editMode ? storySubtitle : '');

    const displayInMixTabByStoryTabType = useMemo(() => {
      switch (storyTabType) {
        case StoryTabType.storyTab1:
          return { id: DisplayProps.includeInMixTab, value: DisplayProps.includeInMixTab };
        case StoryTabType.storyTab2:
          return { id: DisplayProps.includeInMixTab2, value: DisplayProps.includeInMixTab2 };
        case StoryTabType.storyTab3:
          return { id: DisplayProps.includeInMixTab3, value: DisplayProps.includeInMixTab3 };
        case StoryTabType.storyTab4:
          return { id: DisplayProps.includeInMixTab4, value: DisplayProps.includeInMixTab4 };
        case StoryTabType.storyTab5:
          return { id: DisplayProps.includeInMixTab5, value: DisplayProps.includeInMixTab5 };
      }
    }, [storyTabType]);

    const getDisplayItems = useMemo(() => {
      if (!editMode) {
        return [
          { id: DisplayProps.includeInWebappNewsFeed, value: DisplayProps.includeInWebappNewsFeed },
          { id: DisplayProps.includeInNewsFeed, value: DisplayProps.includeInNewsFeed },
          { ...displayInMixTabByStoryTabType },
        ];
      }

      return [
        ...optArrItem(storyIncludeInWebappNewsFeed, {
          id: DisplayProps.includeInWebappNewsFeed,
          value: DisplayProps.includeInWebappNewsFeed,
        }),
        ...optArrItem(storyIncludeInNewsFeed, {
          id: DisplayProps.includeInNewsFeed,
          value: DisplayProps.includeInNewsFeed,
        }),
        ...optArrItem(storyIncludeInMixTab, {
          id: DisplayProps.includeInMixTab,
          value: DisplayProps.includeInMixTab,
        }),
        ...optArrItem(storyIncludeInMixTab2, {
          id: DisplayProps.includeInMixTab2,
          value: DisplayProps.includeInMixTab2,
        }),
        ...optArrItem(storyIncludeInMixTab3, {
          id: DisplayProps.includeInMixTab3,
          value: DisplayProps.includeInMixTab3,
        }),
        ...optArrItem(storyIncludeInMixTab4, {
          id: DisplayProps.includeInMixTab4,
          value: DisplayProps.includeInMixTab4,
        }),
        ...optArrItem(storyIncludeInMixTab5, {
          id: DisplayProps.includeInMixTab5,
          value: DisplayProps.includeInMixTab5,
        }),
        ...optArrItem(Boolean(storyPinned), {
          id: DisplayProps.pinned,
          value: storyPinned,
        }),
        ...optArrItem(Boolean(storyPinned2), {
          id: DisplayProps.pinned2,
          value: storyPinned2,
        }),
        ...optArrItem(Boolean(storyPinned3), {
          id: DisplayProps.pinned3,
          value: storyPinned3,
        }),
        ...optArrItem(Boolean(storyPinned4), {
          id: DisplayProps.pinned4,
          value: storyPinned4,
        }),
        ...optArrItem(Boolean(storyPinned5), {
          id: DisplayProps.pinned5,
          value: storyPinned5,
        }),
        ...optArrItem(Boolean(storyHorizontalDisplayView), {
          id: DisplayProps.horizontalPosition,
          value: storyHorizontalDisplayView?.position,
        }),
      ];
    }, [
      displayInMixTabByStoryTabType,
      editMode,
      storyHorizontalDisplayView,
      storyIncludeInMixTab,
      storyIncludeInMixTab2,
      storyIncludeInMixTab3,
      storyIncludeInMixTab4,
      storyIncludeInMixTab5,
      storyIncludeInNewsFeed,
      storyIncludeInWebappNewsFeed,
      storyPinned,
      storyPinned2,
      storyPinned3,
      storyPinned4,
      storyPinned5,
    ]);

    const getCardItems = useMemo(() => {
      if (!editMode) {
        return [
          { id: CardProps.allowComments, value: CardProps.allowComments },
          { id: CardProps.allowCardReactions, value: CardProps.allowCardReactions },
        ];
      }

      return [
        ...optArrItem(storyAllowComments, {
          id: CardProps.allowComments,
          value: CardProps.allowComments,
        }),
        ...optArrItem(storyAllowCardReactions, {
          id: CardProps.allowCardReactions,
          value: CardProps.allowCardReactions,
        }),
        ...optArrItem(storyAllowSharing, {
          id: CardProps.allowSharing,
          value: CardProps.allowSharing,
        }),
        ...optArrItem(storyShouldUpdatePostedTimeByComments, {
          id: CardProps.shouldUpdatePostedTimeByComments,
          value: CardProps.shouldUpdatePostedTimeByComments,
        }),
        ...optArrItem(storyShowAllAuthors, {
          id: CardProps.showAllAuthors,
          value: CardProps.showAllAuthors,
        }),
        ...optArrItem(storyAllowCardMentions, {
          id: CardProps.allowCardMentions,
          value: CardProps.allowCardMentions,
        }),
      ];
    }, [
      editMode,
      storyAllowCardMentions,
      storyAllowCardReactions,
      storyAllowComments,
      storyAllowSharing,
      storyShouldUpdatePostedTimeByComments,
      storyShowAllAuthors,
    ]);

    const getCardCommentItems = useMemo(() => {
      const value = editMode
        ? storyHighlightingComments
        : StoryHighlightingCommentsMode.MOST_LIKEABLE;

      return [
        { id: StoryHighlightingCommentsMode[value], value: StoryHighlightingCommentsMode[value] },
      ];
    }, [editMode, storyHighlightingComments]);

    const getCardTeaserItems = useMemo(() => {
      const value = editMode
        ? storyCardStyles?.article?.teaserStyle
        : StoryCardTeaserImageStyle.STANDARD;

      return [{ id: StoryCardTeaserImageStyle[value], value: StoryCardTeaserImageStyle[value] }];
    }, [editMode, storyCardStyles?.article?.teaserStyle]);

    const getAccessItems = useMemo(() => {
      if (!editMode) {
        return [];
      }

      return [
        ...optArrItem(storyAllowAccessToEditorLimited, {
          id: StoryAccess.EDITOR_LIMITED,
          value: StoryAccess.EDITOR_LIMITED,
        }),
        ...optArrItem(storyAllowAccessToReader, {
          id: StoryAccess.READER,
          value: StoryAccess.READER,
        }),
      ];
    }, [editMode, storyAllowAccessToEditorLimited, storyAllowAccessToReader]);

    const getOtherSyncItems = useMemo(() => {
      if (!editMode) {
        return [];
      }

      return [
        ...optArrItem(storyShouldSyncItemPositionsOnConnectedStories, {
          id: OtherProps.shouldSyncItemPositionsOnConnectedStories,
          value: OtherProps.shouldSyncItemPositionsOnConnectedStories,
        }),
      ];
    }, [editMode, storyShouldSyncItemPositionsOnConnectedStories]);

    const getOtherTimeItems = useMemo(() => {
      const value = editMode
        ? storyDisplayItemUpdatedTime
        : StoryDisplayItemUpdatedTime.POSTED_TIME;

      return [
        { id: StoryDisplayItemUpdatedTime[value], value: StoryDisplayItemUpdatedTime[value] },
      ];
    }, [editMode, storyDisplayItemUpdatedTime]);

    const getOtherCardOrderItems = useMemo(() => {
      const value = editMode ? storyCardOrder : StoryCardOrder.MANUAL;

      return [{ id: StoryCardOrder[value], value: StoryCardOrder[value] }];
    }, [editMode, storyCardOrder]);

    const restrictDuplicateDisabled = useMemo(
      () => channelRestrictDuplicateUrlOfCardsGQL !== RestrictDuplicateUrlOfCardsInTimeFrame.NEVER,
      [channelRestrictDuplicateUrlOfCardsGQL]
    );

    const restrictDuplicateValue = useMemo(() => {
      if (restrictDuplicateDisabled) {
        return [
          { value: RestrictDuplicateUrlOfCardsInTimeFrame[channelRestrictDuplicateUrlOfCardsGQL] },
        ];
      }

      return [
        {
          value:
            RestrictDuplicateUrlOfCardsInTimeFrame[
              storyShouldRestrictDuplicateUrlOfCardsInTimeFrame
            ] ?? RestrictDuplicateUrlOfCardsInTimeFrame.NEVER,
        },
      ];
    }, [
      channelRestrictDuplicateUrlOfCardsGQL,
      restrictDuplicateDisabled,
      storyShouldRestrictDuplicateUrlOfCardsInTimeFrame,
    ]);

    const cardLimitValue = useMemo(() => {
      return [{ value: StoryCardLimit[storyCardLimit] ?? StoryCardLimit.ONE_THOUSAND }];
    }, [storyCardLimit]);

    const feedFollowBehaviorValue = useMemo(() => {
      return [
        { value: FeedFollowBehavior[storyFeedFollowBehavior] ?? FeedFollowBehavior.MANDATORY },
      ];
    }, [storyFeedFollowBehavior]);

    const [settings, setSettings] = useState<StorySettings>({
      display: [{ groupId: 'display', groupItems: getDisplayItems }],
      card: [
        ...optArrItem(isStandard, {
          groupId: 'card',
          groupItems: getCardItems,
        }),
        ...optArrItem(isStandard, {
          groupId: 'highlightingComments',
          groupItems: getCardCommentItems,
        }),
        ...optArrItem(isStandard, {
          groupId: 'teaserStyle',
          groupItems: getCardTeaserItems,
        }),
        ...optArrItem(isStandard, {
          groupId: 'localeId',
          groupItems: [{ value: storyLocaleId ?? channelLocaleId }],
        }),
        ...optArrItem(isStandard, {
          groupId: 'backgroundColor',
          groupItems: [{ value: storyCardStyles?.backgroundColor ?? '#ffffff' }],
        }),
      ],
      tags: {
        external: externalTags,
        setExternal: setExternalTags,
        externalStatus: externalStatusTags,
        setExternalStatus: setExternalStatusTags,
        internal: internalTags,
        setInternal: setInternalTags,
      },
      access: [
        ...optArrItem(isStandard, {
          groupId: 'access',
          groupItems: getAccessItems,
        }),
      ],
      writeAccess: { userTags, setUserTags, users, setUsers },
      other: [
        ...optArrItem(isStandard, {
          groupId: 'shouldSyncItemPositionsOnConnectedStories',
          groupItems: getOtherSyncItems,
        }),
        ...optArrItem(isStandard, {
          groupId: 'displayItemUpdatedTime',
          groupItems: getOtherTimeItems,
        }),
        ...optArrItem(isStandard, {
          groupId: 'cardOrder',
          groupItems: getOtherCardOrderItems,
        }),
        ...optArrItem(isStandard, {
          groupId: 'shouldRestrictDuplicateUrlOfCardsInTimeFrame',
          groupItems: restrictDuplicateValue,
        }),
        ...optArrItem(isStandard, {
          groupId: 'cardLimit',
          groupItems: cardLimitValue,
        }),
        ...optArrItem(isStandard, {
          groupId: 'feedFollowBehavior',
          groupItems: feedFollowBehaviorValue,
        }),
      ],
      autoNotification: { autoNotification, setAutoNotification },
      sync: { storySync, setStorySync, setTitle, setSubtitle, setGalleryItems },
    });

    useEffect(() => {
      setSettings({
        ...settings,
        tags: {
          external: externalTags,
          setExternal: setExternalTags,
          externalStatus: externalStatusTags,
          setExternalStatus: setExternalStatusTags,
          internal: internalTags,
          setInternal: setInternalTags,
        },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalTags, externalStatusTags, internalTags]);

    useEffect(() => {
      setSettings({
        ...settings,
        writeAccess: { userTags, setUserTags, users, setUsers },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userTags, users]);

    useEffect(() => {
      setSettings({ ...settings, autoNotification: { autoNotification, setAutoNotification } });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoNotification]);

    useEffect(() => {
      setSettings({
        ...settings,
        sync: { storySync, setStorySync, setTitle, setSubtitle, setGalleryItems },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storySync]);

    const { getGroupValue, getGroupItemValue } = useFilter();

    const { display, card, access, other } = settings;

    const [loading, setLoading] = useState<boolean>(false);

    const [settingsOpen, setSettingsOpen] = useState<boolean>(false);

    const isAutoNotificationValid = useMemo(() => {
      const { enabled, titleDefault, messageDefault } = autoNotification;

      if (!enabled) {
        return true;
      }

      return Boolean(
        titleDefault.length >= AUTO_NOTIFICATION_TITLE_MIN_LIMIT &&
          messageDefault.length >= AUTO_NOTIFICATION_MESSAGE_MIN_LIMIT
      );
    }, [autoNotification]);

    const syncSources = useMemo(() => {
      if (!storySync.enabled) {
        return [];
      }

      return storySync.sources.flatMap(({ storyId, fetchLastCards }) =>
        storyId ? [{ storyId, ...(!editMode && { fetchLastCards }) }] : []
      );
    }, [editMode, storySync.enabled, storySync.sources]);

    const saveDisabled = useMemo(() => {
      if (loading || !title.length || !isAutoNotificationValid) {
        return true;
      }

      if (noStandardNorEdit) {
        return !syncSources.length;
      }

      return false;
    }, [isAutoNotificationValid, loading, noStandardNorEdit, syncSources.length, title.length]);

    const toggleStoryDisable = useCallback(
      ({ disabled }: { disabled: boolean }) => {
        if (!editMode) {
          return;
        }

        document.querySelectorAll(`.story-${storyId}`).forEach((storyElement) => {
          disabled
            ? storyElement.classList.add('story-disabled')
            : storyElement.classList.remove('story-disabled');
        });
      },
      [editMode, storyId]
    );

    const saveStory = useCallback(async () => {
      toggleStoryDisable({ disabled: true });
      onClose();

      const displayGroup = { filter: display, groupId: 'display' };

      const includeInWebappNewsFeed = Boolean(
        getGroupItemValue({ ...displayGroup, itemId: DisplayProps.includeInWebappNewsFeed })
      );

      const includeInNewsFeed = Boolean(
        getGroupItemValue({ ...displayGroup, itemId: DisplayProps.includeInNewsFeed })
      );

      const includeInMixTab = Boolean(
        getGroupItemValue({ ...displayGroup, itemId: DisplayProps.includeInMixTab })
      );

      const includeInMixTab2 = Boolean(
        getGroupItemValue({ ...displayGroup, itemId: DisplayProps.includeInMixTab2 })
      );

      const includeInMixTab3 = Boolean(
        getGroupItemValue({ ...displayGroup, itemId: DisplayProps.includeInMixTab3 })
      );

      const includeInMixTab4 = Boolean(
        getGroupItemValue({ ...displayGroup, itemId: DisplayProps.includeInMixTab4 })
      );

      const includeInMixTab5 = Boolean(
        getGroupItemValue({ ...displayGroup, itemId: DisplayProps.includeInMixTab5 })
      );

      const pinned = getGroupItemValue({ ...displayGroup, itemId: DisplayProps.pinned }) ?? null;

      const pinned2 = getGroupItemValue({ ...displayGroup, itemId: DisplayProps.pinned2 }) ?? null;

      const pinned3 = getGroupItemValue({ ...displayGroup, itemId: DisplayProps.pinned3 }) ?? null;

      const pinned4 = getGroupItemValue({ ...displayGroup, itemId: DisplayProps.pinned4 }) ?? null;

      const pinned5 = getGroupItemValue({ ...displayGroup, itemId: DisplayProps.pinned5 }) ?? null;

      const position = getGroupItemValue({
        ...displayGroup,
        itemId: DisplayProps.horizontalPosition,
      });

      const cardGroup = { filter: card, groupId: 'card' };

      const allowComments = Boolean(
        getGroupItemValue({ ...cardGroup, itemId: CardProps.allowComments })
      );

      const allowCardMentions = Boolean(
        getGroupItemValue({ ...cardGroup, itemId: CardProps.allowCardMentions })
      );

      const allowCardReactions = Boolean(
        getGroupItemValue({ ...cardGroup, itemId: CardProps.allowCardReactions })
      );

      const allowSharing = Boolean(
        getGroupItemValue({ ...cardGroup, itemId: CardProps.allowSharing })
      );

      const shouldUpdatePostedTimeByComments = Boolean(
        getGroupItemValue({ ...cardGroup, itemId: CardProps.shouldUpdatePostedTimeByComments })
      );

      const showAllAuthors = Boolean(
        getGroupItemValue({ ...cardGroup, itemId: CardProps.showAllAuthors })
      );

      const localeId = getGroupValue({ filter: card, groupId: 'localeId' });

      const backgroundColor = getGroupValue({ filter: card, groupId: 'backgroundColor' });

      const highlightingComments = getGroupValue({ filter: card, groupId: 'highlightingComments' });

      const teaserStyle = getGroupValue({ filter: card, groupId: 'teaserStyle' });

      const accessValue = getGroupValue({ filter: access, groupId: 'access', multiCheck: true });

      const shouldSyncItemPositionsOnConnectedStories = Boolean(
        getGroupValue({ filter: other, groupId: 'shouldSyncItemPositionsOnConnectedStories' })
      );

      const cardOrder = getGroupValue({ filter: other, groupId: 'cardOrder' });

      const displayItemUpdatedTime = getGroupValue({
        filter: other,
        groupId: 'displayItemUpdatedTime',
      });

      const shouldRestrictDuplicateUrlOfCardsInTimeFrame = getGroupValue({
        filter: other,
        groupId: 'shouldRestrictDuplicateUrlOfCardsInTimeFrame',
      });

      const cardLimit = getGroupValue({ filter: other, groupId: 'cardLimit' });

      const feedFollowBehavior = getGroupValue({ filter: other, groupId: 'feedFollowBehavior' });

      const fields: StoryCreateFieldsInput = {
        title,
        subtitle,
        pinned,
        pinned2,
        pinned3,
        pinned4,
        pinned5,
        imageId: galleryItemImage?.id ?? null,
        imageRightholder: galleryItemImage?.rightholder ?? '',
        ...(isStandard && {
          localeId,
          organisationTags: {
            externalTagsId: externalTags.map(({ id }) => id),
            externalStatusTagsId: externalStatusTags.map(({ id }) => id),
            internalTagsId: internalTags.map(({ id }) => id),
          },
          writeAccess: {
            userTagIds: userTags.map(({ id }) => id),
            userIds: users.map(({ id }) => id),
          },
        }),
        source: {
          ...(!editMode && { isReadOnly: !isStandard }),
          sources: syncSources,
        },
        settings: {
          includeInWebappNewsFeed,
          includeInNewsFeed,
          includeInMixTab,
          includeInMixTab2,
          includeInMixTab3,
          includeInMixTab4,
          includeInMixTab5,
          horizontalDisplayView: position ? { position } : null,
          ...(isStandard && {
            allowComments,
            allowCardMentions,
            allowCardReactions,
            allowSharing,
            shouldUpdatePostedTimeByComments,
            showAllAuthors,
            autoNotification,
            cardLimit,
            highlightingComments,
            cardStyles: { article: { teaserStyle }, backgroundColor },
            access: accessValue,
            cardOrder,
            feedFollowBehavior,
            shouldSyncItemPositionsOnConnectedStories,
            displayItemUpdatedTime,
            shouldRestrictDuplicateUrlOfCardsInTimeFrame,
          }),
        },
      };

      const storyCreateArgs = { channelId, fields };

      const storyUpdateArgs = { storyId, fields } as StoryUpdateArgs;

      const action = editMode ? 'edit' : 'create';

      try {
        const story = await toast.promise(
          (editMode
            ? dispatch(updateStory(storyUpdateArgs))
            : storyCreate(storyCreateArgs)
          ).unwrap(),
          { pending: t(`addStory.${action}-pending`), success: t(`addStory.${action}-success`) }
        );

        if (!story) {
          return;
        }

        refreshFeeds();

        callback?.(story);
      } catch (_) {
        toast.error(t(`addStory.${action}-error`));
      } finally {
        toggleStoryDisable({ disabled: false });
      }
    }, [
      access,
      autoNotification,
      callback,
      card,
      channelId,
      dispatch,
      display,
      editMode,
      externalStatusTags,
      externalTags,
      galleryItemImage?.id,
      galleryItemImage?.rightholder,
      getGroupItemValue,
      getGroupValue,
      internalTags,
      isStandard,
      onClose,
      other,
      refreshFeeds,
      storyCreate,
      storyId,
      subtitle,
      syncSources,
      t,
      title,
      toggleStoryDisable,
      userTags,
      users,
    ]);

    const readOnlyContent = useMemo(() => {
      if (standardOrEdit) {
        return null;
      }

      return (
        <>
          <div className={classes['create-story__subTitle']}>
            {t('createStory.modal-sub-title')}
          </div>

          <StorySync
            storySync={storySync}
            setStorySync={setStorySync}
            setTitle={setTitle}
            setSubtitle={setSubtitle}
            setGalleryItems={setGalleryItems}
          />
        </>
      );
    }, [storySync, standardOrEdit, t]);

    const modalTitle = t(`${editMode ? 'update' : 'create'}Story.modal-title`, {
      type: t(`common.${storyType.toLowerCase()}`).toLowerCase(),
    });

    const body = useMemo(() => {
      return (
        <div className={classes['create-story']}>
          <div className={classes['create-story__title']}>{modalTitle}</div>
          {readOnlyContent}
          <div className={classes['create-story__content']}>
            <FileUpload
              galleryItems={galleryItems}
              setGalleryItems={setGalleryItems}
              loading={loading}
              setLoading={setLoading}
              fileAcceptTypes={[FileAcceptType.IMAGE]}
              uploadLimit={1}
            />

            <FilePreview
              galleryItems={galleryItems}
              setGalleryItems={setGalleryItems}
              hasSettings
              hideCaption
            />

            <div className={classes['create-story__content-fields']}>
              <TextAreaField
                inputClassName={classes['create-story__content-fields-title']}
                value={title}
                placeholder={t('createStory.title-placeholder')}
                maxLength={160}
                onChange={({ target }) => setTitle(target.value)}
                disableLineBreak
              />

              <TextAreaField
                inputClassName={classes['create-story__content-fields-intro']}
                value={subtitle}
                placeholder={t('createStory.subtitle-placeholder')}
                maxLength={160}
                onChange={({ target }) => setSubtitle(target.value)}
                disableLineBreak
              />
            </div>
          </div>
          <div className={classes['create-story__buttons']}>
            <IconLabelWithActive
              active={settingsOpen}
              setActive={setSettingsOpen}
              {...(!isMobileOnly && { label: t('common.settings') })}
              iconId={'settings'}
              ariaLabel={t('common.settings')}
              className={classes['create-story__buttons-settings']}
            />

            <Button type={ButtonType.secondary} label={t('common.cancel')} onClick={onClose} />

            <Button
              type={ButtonType.primary}
              label={t(`common.${editMode ? 'update' : 'create'}`)}
              onClick={saveStory}
              disabled={saveDisabled}
            />
          </div>

          {settingsOpen && (
            <CreateStorySettings
              storyId={storyId}
              settings={settings}
              onChange={setSettings}
              isStandard={isStandard}
              activeSettingsTabIndex={activeSettingsTabIndex}
              setActiveSettingsTabIndex={setActiveSettingsTabIndex}
            />
          )}
        </div>
      );
    }, [
      activeSettingsTabIndex,
      editMode,
      galleryItems,
      isStandard,
      loading,
      modalTitle,
      onClose,
      readOnlyContent,
      saveDisabled,
      saveStory,
      settings,
      settingsOpen,
      storyId,
      subtitle,
      t,
      title,
    ]);

    return <Modal isOpen={isOpen} body={body} onClose={onClose} ariaLabel={modalTitle} alignTop />;
  }
);
